/**
 * 分镜快照 —— 「重新编排」的撤销底座(批⑫a 编排页重排安全网)。
 *
 * ═══ 治的是什么病 ═══════════════════════════════════════════════════════════════
 * `triggerComposeJob` 进门第一件事就是把整组分镜字段置 null 再 setForm。用户逐镜手改过的
 * 画面 / 口播 / 时长在点下去的那一瞬间就没了,而且:
 *   - 成功了也回不去 —— 新的一版不一定比旧的好,但旧的已经不存在;
 *   - **失败了更亏** —— catch 只 setSubmitError,分镜已经被清空,一次失败的编排顺手吃掉了上一版。
 * 这里把「将被覆盖的那一版」整份存下来(单槽,会话内;不进 localStorage —— 跨设备/跨标签
 * 的一致性问题不值得为一个撤销键背)。
 *
 * ═══ ★为什么快照字段比「placeholder 清掉的那些」多 ═══════════════════════════════
 * placeholder 只清 15 个字段,但每镜数据散在 FormState 的 **26 条平行数组**里
 * (基准数组 = panelScenes,它的长度就是镜数;见 shotList.ts 头注)。
 * 只还原被清的那一半 = 旧的 5 镜画面配着新的 8 镜运镜/景别/承接 —— 正是 shotList.ts 头注里
 * 记着的那个老病:「全片镜头语言整体错位一格,而且没有任何报错」,而这些数组
 * `buildTaskRequest` 是**原样发出去**的(shotSizes / cameraMovements / sceneIds / factVisuals…)。
 * 所以判据是:**凡与 panelScenes 逐镜对齐的数组,一律整份进快照**,再加上它们指向的
 * storyboard 级注册表(subjects)与编排产出(storyBible / theme / hookPlan / formulaKey…)。
 * `storyboardBackup.test.ts` 用 `formPatchFromShots` 的键集反过来锁这条规矩:
 * 分镜层新增一条平行数组而这里漏了,测试就红。
 *
 * ═══ 不进快照的 ═══════════════════════════════════════════════════════════════
 * - `brandFocus` / `suggestedBgmMood` / `resolvedPurpose` 一族:编排产出没错,但既不被
 *   placeholder 清、也不与镜逐一对齐,还原它们只会让「本次实际按什么编排」显示成上一次的。
 * - 出片期参数(音色 / 字幕 / BGM / 分辨率):它们不属于「某一版分镜」,重编排也不动它们。
 * - `pinnedFactIds`(W4,2026-09-13):它是**编排输入**(「换进去」钉的选料,下一次编排按它
 *   选料),与 briefStyle/briefPurpose 同族 —— 不属于「某一版分镜」的产出,不进快照。
 */
import type { FormState } from "./videoCreateTypes";

/**
 * 一版分镜的完整字段集。
 *
 * 第一组 = 与 panelScenes 逐镜对齐的平行数组(必须与 `formPatchFromShots` 的键集一致);
 * 第二组 = storyboard 级、随这一版分镜一起产出/一起作废的字段。
 */
export const STORYBOARD_BACKUP_KEYS = [
  // ── 逐镜平行数组(基准 = panelScenes)──
  "panelScenes",
  "panelMotions",
  "panelDurations",
  "voiceovers",
  "negativePrompts",
  "panelFirstFrameKeys",
  "panelFirstFrameUrls",
  "panelPreviewKeys",
  "panelPreviewUrls",
  "shotMode",
  "panelBeats",
  "shotSizes",
  "cameraMovements",
  "cameraSpeeds",
  "lightingKeys",
  "shotContinuities",
  "panelSubjectRefs",
  "voiceEmotions",
  "shotIntents",
  "narrativeRoles",
  "heroMoments",
  "sceneIds",
  "factRefs",
  "factVisuals",
  "speechModes",
  "references",
  // ── storyboard 级 ──
  "subjects", // panelSubjectRefs 里的 @token 指向这张注册表,两者必须同进同出
  "storyBible",
  "theme",
  "hookPlan",
  "protagonistAnchorKey",
  "formulaKey",
  "formulaName",
  // W4(2026-09-13):导演交代卡数据,与 storyBible/theme 同族(编排产出,随这一版分镜
  // 一起作废/一起还原)——不进则「恢复上一版分镜」后交代卡显示的是新一版的选料/审片。
  "selectedFacts",
  "unusedFacts",
  "estimatedDurationSec",
  "factTexts",
  "reviewScore",
  "reviewVerdict",
  "reviewMissingInputs",
] as const satisfies readonly (keyof FormState)[];

export type StoryboardBackupKey = (typeof STORYBOARD_BACKUP_KEYS)[number];

/** 一版分镜的快照(FormState 的子集,不含任何非分镜字段)。 */
export type StoryboardBackup = Pick<FormState, StoryboardBackupKey>;

/** 有没有分镜(镜数 = panelScenes 长度;全页唯一判据)。 */
export function hasStoryboardShots(form: Pick<FormState, "panelScenes">): boolean {
  return (form.panelScenes?.length ?? 0) > 0;
}

/**
 * 取一份快照。**没有分镜就返回 null** —— 空快照进槽会让「恢复上一版分镜」入口凭空出现,
 * 点下去把现有分镜清成空(比不给这个键还糟)。
 */
export function captureStoryboardBackup(form: FormState): StoryboardBackup | null {
  if (!hasStoryboardShots(form)) return null;
  const out: Partial<StoryboardBackup> = {};
  for (const k of STORYBOARD_BACKUP_KEYS) {
    // 逐键取值,键集固定 —— 整表 structuredClone 会把非分镜字段一起带走(还原时倒灌)。
    (out as Record<string, unknown>)[k] = form[k];
  }
  return out as StoryboardBackup;
}

/** 把快照写回表单(只覆盖快照里的那些键,其余字段原样保留)。 */
export function applyStoryboardBackup(form: FormState, backup: StoryboardBackup): FormState {
  return { ...form, ...backup };
}
